/**
 * atomberg-product-card.js
 * ────────────────────────
 * Variant switching for atomberg-product-card. Delegates exact + nearest-available
 * variant resolution, current-selection reading, availability recomputation, price
 * formatting, and discount math to the shared `AtombergVariantSelector` module
 * (assets/atomberg-variant-selector.js), which must be loaded first.
 */
(function () {
  /** @type {any} */
  var w = window;
  if (w._atombergProductCardInit) return;

  /** @type {any} */
  var V = w.AtombergVariantSelector;
  if (!V) {
    return;
  }

  w._atombergProductCardInit = true;

  /**
   * @param {HTMLElement} card
   * @returns {Array<any>}
   */
  function getVariants(card) {
    var raw = card.getAttribute('data-variants');
    if (!raw) return [];
    try {
      return JSON.parse(raw);
      // eslint-disable-next-line no-unused-vars
    } catch (e) {
      return [];
    }
  }

  /**
   * @param {string} base
   * @param {string|number} variantId
   */
  function buildVariantUrl(base, variantId) {
    if (!base) return '';
    var sep = base.indexOf('?') === -1 ? '?' : '&';
    return base + sep + 'variant=' + variantId;
  }

  /**
   * Apply a new variant's data to the card UI.
   * @param {HTMLElement} card
   * @param {any} variant
   * @param {Array<any>} variants
   */
  function updateCard(card, variant, variants) {
    if (!variant) return;
    showCardError(card, '');
    var currencySymbol = card.getAttribute('data-currency-symbol') || '';
    var showDecimals = card.getAttribute('data-show-decimals') === '1';
    var productUrl = card.getAttribute('data-product-url') || '';

    var vOpts = [variant.option1 || null, variant.option2 || null, variant.option3 || null];

    card.querySelectorAll('[data-option-index]').forEach(function (group) {
      var idx = parseInt(group.getAttribute('data-option-index') || '0', 10);
      var target = vOpts[idx];

      group.querySelectorAll('.atomberg-swatch').forEach(function (sw) {
        sw.classList.toggle(
          'atomberg-swatch--active',
          sw.getAttribute('data-option-value') === target
        );
      });
      group.querySelectorAll('.atomberg-chip').forEach(function (ch) {
        var isActive = ch.getAttribute('data-option-value') === target;
        ch.classList.toggle('atomberg-chip--active', isActive);
        ch.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    });

    var colorNameEl = card.querySelector('[data-color-selected-name]');
    if (colorNameEl) {
      var swatchGroup = card.querySelector('.atomberg-swatches[data-option-index]');
      var activeSwatch = swatchGroup && swatchGroup.querySelector('.atomberg-swatch--active');
      if (activeSwatch)
        colorNameEl.textContent = activeSwatch.getAttribute('data-option-value') || '';
    }

    var img = /** @type {HTMLImageElement|null} */ (card.querySelector('[data-card-image]'));
    if (img && variant.featured_image) {
      // Rebuild srcset too — with a srcset present the browser ignores a bare src swap
      var imgBase = variant.featured_image.src.replace(/\?.*$/, '');
      img.srcset = [165, 250, 360, 500, 600]
        .map(function (w) {
          return imgBase + '?width=' + w + ' ' + w + 'w';
        })
        .join(', ');
      img.src = imgBase + '?width=500';
      img.alt = variant.featured_image.alt || '';
    }

    var priceEl = card.querySelector('[data-card-price]');
    if (priceEl) priceEl.textContent = V.formatPrice(variant.price, currencySymbol, showDecimals);

    var hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;

    var compareEl = /** @type {HTMLElement|null} */ (
      card.querySelector('[data-card-compare-price]')
    );
    if (compareEl) {
      if (hasCompare) {
        compareEl.textContent =
          'M.R.P ' + V.formatPrice(variant.compare_at_price, currencySymbol, showDecimals);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    var discountEl = /** @type {HTMLElement|null} */ (card.querySelector('[data-card-discount]'));
    if (discountEl) {
      if (hasCompare) {
        discountEl.textContent = V.discountPercent(variant) + '% OFF';
        discountEl.hidden = false;
      } else {
        discountEl.hidden = true;
      }
    }

    var taxEl = /** @type {HTMLElement|null} */ (card.querySelector('[data-card-tax]'));
    if (taxEl) taxEl.hidden = !hasCompare;

    var variantInput = card.querySelector('[data-card-variant-id]');
    if (variantInput) variantInput.setAttribute('data-card-variant-id', String(variant.id));

    var titleLink = /** @type {HTMLAnchorElement|null} */ (
      card.querySelector('[data-card-title-link]')
    );
    if (titleLink) titleLink.href = buildVariantUrl(productUrl, variant.id);

    var titleEl = card.querySelector('[data-card-title]');
    if (titleEl) {
      /** @type {Record<string, string>} */
      var titles = {};
      try {
        titles = JSON.parse(card.getAttribute('data-variant-titles') || '{}');
        // eslint-disable-next-line no-unused-vars
      } catch (e) {
        titles = {};
      }
      var newTitle = titles[String(variant.id)] || card.getAttribute('data-product-title');
      if (newTitle) titleEl.textContent = newTitle;
    }

    var imageLink = /** @type {HTMLAnchorElement|null} */ (
      card.querySelector('.atomberg-product-card__image-link')
    );
    if (imageLink) imageLink.href = buildVariantUrl(productUrl, variant.id);

    var atcBtn = /** @type {HTMLButtonElement|null} */ (
      card.querySelector('.atomberg-product-card__btn--atc')
    );
    if (atcBtn) {
      var atcLabel = card.getAttribute('data-add-to-cart-label') || 'Add to Cart';
      atcBtn.disabled = !variant.available;
      // Inner text lives in .add-to-cart-text__content to keep the checkmark markup intact
      var atcTextEl = atcBtn.querySelector('.add-to-cart-text__content');
      if (atcTextEl) {
        atcTextEl.textContent = variant.available ? atcLabel : 'Sold out';
      }
    }

    V.recomputeAvailability(card, variants, vOpts);

    card.dispatchEvent(
      new CustomEvent('atomberg:variant-change', {
        bubbles: true,
        detail: { variant: variant },
      })
    );
  }

  /**
   * Initial pass per card: title link href + availability classes.
   * @param {HTMLElement} card
   */
  function initCard(card) {
    var variants = getVariants(card);
    var opts = V.readSelections(card);
    V.recomputeAvailability(card, variants, opts);

    var productUrl = card.getAttribute('data-product-url') || '';
    var variantEl = card.querySelector('[data-card-variant-id]');
    var variantId = variantEl ? variantEl.getAttribute('data-card-variant-id') : null;

    var titleLink = /** @type {HTMLAnchorElement|null} */ (
      card.querySelector('[data-card-title-link]')
    );
    if (titleLink && variantId) {
      titleLink.href = buildVariantUrl(productUrl, variantId);
    }

    var imageLink = /** @type {HTMLAnchorElement|null} */ (
      card.querySelector('.atomberg-product-card__image-link')
    );
    if (imageLink && variantId) {
      imageLink.href = buildVariantUrl(productUrl, variantId);
    }
  }

  /**
   * Triggers Horizon's native fly-to-cart animation.
   * <fly-to-cart> is a Horizon custom element defined in fly-to-cart.js;
   * it reads .source / .destination positions and animates along a bezier curve.
   * @param {HTMLElement} btn - the ATC button (animation origin)
   * @param {string} imageSrc - product image URL for the flying thumbnail
   */
  function flyToCart(btn, imageSrc) {
    var cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !imageSrc) return;

    var el = /** @type {any} */ (document.createElement('fly-to-cart'));
    el.classList.add('fly-to-cart--main');
    el.style.setProperty('background-image', 'url(' + imageSrc + ')');
    el.style.setProperty('--start-opacity', '0');
    el.source = btn;
    el.destination = cartIcon;
    document.body.appendChild(el);
  }

  /**
   * Plays Horizon's native checkmark-burst animation on the button then reverts.
   * Requires the button to have the .add-to-cart-button class and inner
   * .add-to-cart-text / .add-to-cart__added markup (set in the Liquid).
   * @param {HTMLButtonElement} btn
   */
  function animateBtn(btn) {
    // Re-enable before setting data-added — the button was disabled during the AJAX
    // request and :disabled applies opacity:0.5, which grays the button while the
    // checkmark animation plays.
    btn.disabled = false;
    btn.setAttribute('data-added', 'true');
    setTimeout(function () {
      btn.removeAttribute('data-added');
    }, 1200);
  }

  /**
   * Shows an error inline in the card, below the ATC button — matching
   * Horizon's native product-form error style. Persistent until cleared.
   * @param {HTMLElement | null} card
   * @param {string} message
   */
  function showCardError(card, message) {
    if (!card) return;
    var err = /** @type {HTMLElement|null} */ (card.querySelector('.atomberg-product-card__error'));
    var txt = err && err.querySelector('.atomberg-product-card__error-text');
    if (!err || !txt) return;
    txt.textContent = message || '';
    err.hidden = !message;
  }

  /**
   * Reads the cart once to resolve everything the add needs:
   *  - properties: inherit the existing line's properties so the add MERGES
   *    (Shopify only merges lines whose properties match exactly); otherwise
   *    mirror a fresh PDP add (installation property for eligible products).
   *  - currentQty: the variant's existing cart quantity, for the stock check.
   * Falls back gracefully if /cart.js fails.
   * @param {string} variantId
   * @param {boolean} installationEligible
   * @returns {Promise<{ properties: Record<string, string> | null, currentQty: number }>}
   */
  function resolveCartContext(variantId, installationEligible) {
    var fallbackProps = installationEligible ? { _is_installation_request: 'false' } : null;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var existing = (cart.items || []).find(function (/** @type {any} */ it) {
          return String(it.variant_id) === String(variantId);
        });
        if (existing) {
          var props =
            existing.properties && Object.keys(existing.properties).length
              ? existing.properties
              : null;
          return { properties: props, currentQty: existing.quantity || 0 };
        }
        return { properties: fallbackProps, currentQty: 0 };
      })
      .catch(function () {
        return { properties: fallbackProps, currentQty: 0 };
      });
  }

  /**
   * Available stock for a variant, or null when the variant is untracked /
   * unlimited. Read from the data-variant-inventory map (tracked variants only).
   * @param {HTMLElement} card
   * @param {string} variantId
   * @returns {number | null}
   */
  function variantStock(card, variantId) {
    var raw = card.getAttribute('data-variant-inventory');
    if (!raw) return null;
    try {
      var map = JSON.parse(raw);
      return Object.prototype.hasOwnProperty.call(map, variantId) ? Number(map[variantId]) : null;
    } catch (e) {
      return null;
    }
  }

  // ATC button click — button is type="button" so no form submit/redirect ever fires.
  document.addEventListener('click', function (event) {
    var target = /** @type {HTMLElement} */ (event.target);
    var rawBtn = target.closest('[data-card-atc]');
    if (!rawBtn) return;

    // Narrow to HTMLButtonElement — TS won't carry the rawBtn guard into closures,
    // so we store a typed local that's safe to reference inside .then()/.catch().
    var btn = /** @type {HTMLButtonElement} */ (rawBtn);
    if (btn.disabled) return;

    var card = /** @type {HTMLElement|null} */ (btn.closest('.atomberg-product-card'));
    if (!card) return;

    // Variant ID lives on the button as a data attribute (no hidden input)
    var variantId = btn.getAttribute('data-card-variant-id');
    if (!variantId) return;

    btn.disabled = true;
    var cardImg = /** @type {HTMLImageElement|null} */ (card.querySelector('[data-card-image]'));
    var imageSrc = cardImg ? cardImg.src : '';
    var installationEligible = card.getAttribute('data-installation-eligible') === '1';
    var stock = variantStock(card, variantId);

    // Match the line item properties an existing line (or the PDP) would carry so
    // /cart/add.js MERGES instead of creating a duplicate line. The PDP always
    // submits properties[_is_installation_request] for installation-eligible
    // products; a bare add (no properties) never merges with those lines.
    resolveCartContext(variantId, installationEligible)
      .then(function (ctx) {
        // Stock guard: block before the request when the cart already holds all
        // available units of a tracked variant. Server 422 stays the backstop.
        if (stock != null && ctx.currentQty + 1 > stock) {
          btn.disabled = false;
          showCardError(
            card,
            stock <= 0
              ? 'This item is out of stock.'
              : 'Only ' + stock + ' available - already in your cart.'
          );
          return null;
        }
        /** @type {{ id: string|null, quantity: number, properties?: Record<string, string> }} */
        var body = { id: variantId, quantity: 1 };
        if (ctx.properties) body.properties = ctx.properties;
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }).then(function (r) {
          // /cart/add.js resolves r.json() for any status; a 422 (no more stock)
          // carries Shopify's limit message in `description`.
          return r.json().then(function (data) {
            return { ok: r.ok, data: data };
          });
        });
      })
      .then(function (res) {
        if (!res) return; // blocked by the stock guard above
        if (!res.ok) {
          // Inventory cap reached server-side — surface Shopify's message.
          btn.disabled = false;
          showCardError(card, res.data.description || res.data.message || '');
          return;
        }
        showCardError(card, '');
        flyToCart(btn, imageSrc);
        animateBtn(btn);
        // Dispatch cart:update matching Horizon's CartAddEvent shape so that:
        // - cart-icon.js increments the badge (source: 'product-form-component' triggers additive mode)
        // - component-cart-items.js re-renders the drawer (detail.data must not be null)
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              data: {
                itemCount: 1,
                source: 'product-form-component',
              },
            },
          })
        );
      })
      .catch(function () {
        btn.disabled = false;
      });
  });

  document.addEventListener('click', function (event) {
    var target = /** @type {HTMLElement} */ (event.target);
    var btn = target.closest('.atomberg-swatch, .atomberg-chip');
    if (!btn) return;

    var card = /** @type {HTMLElement|null} */ (btn.closest('.atomberg-product-card'));
    if (!card) return;

    var group = /** @type {HTMLElement|null} */ (btn.closest('[data-option-index]'));
    if (!group) return;

    var optionIdx = parseInt(group.getAttribute('data-option-index') || '0', 10);
    var optionValue = btn.getAttribute('data-option-value');
    if (!optionValue) return;

    var variants = getVariants(card);
    var opts = V.readSelections(card);
    var best = V.findBestAvailable(variants, opts, optionIdx, optionValue);
    if (best) updateCard(card, best, variants);
  });

  function start() {
    document.querySelectorAll('.atomberg-product-card').forEach(function (el) {
      initCard(/** @type {HTMLElement} */ (el));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
